import type {
  FacilitiesState,
  FacilityEffects,
  FacilityLevel,
  FacilityType,
  FacilityUpgrade,
  GameState,
} from './types'

const EMPTY_EFFECTS: FacilityEffects = {
  developmentCapacityBonus: 0,
  medicalRecoveryBonus: 0,
  injuryRiskReduction: 0,
  stadiumCapacity: 15_000,
  weeklyRevenue: 0,
  academyQuality: 0,
}

const effects = (overrides: Partial<FacilityEffects>): FacilityEffects => ({ ...EMPTY_EFFECTS, ...overrides })

export const FACILITY_CATALOGUE: Record<FacilityType, readonly FacilityUpgrade[]> = {
  'training-ground': [
    { facility: 'training-ground', level: 1, cost: 4, buildWeeks: 2, effects: effects({ developmentCapacityBonus: 1 }) },
    { facility: 'training-ground', level: 2, cost: 8, buildWeeks: 3, effects: effects({ developmentCapacityBonus: 2 }) },
    { facility: 'training-ground', level: 3, cost: 14, buildWeeks: 4, effects: effects({ developmentCapacityBonus: 4 }) },
  ],
  'medical-centre': [
    { facility: 'medical-centre', level: 1, cost: 3, buildWeeks: 2, effects: effects({ medicalRecoveryBonus: 5, injuryRiskReduction: 3 }) },
    { facility: 'medical-centre', level: 2, cost: 7, buildWeeks: 3, effects: effects({ medicalRecoveryBonus: 10, injuryRiskReduction: 7 }) },
    { facility: 'medical-centre', level: 3, cost: 12, buildWeeks: 4, effects: effects({ medicalRecoveryBonus: 18, injuryRiskReduction: 12 }) },
  ],
  stadium: [
    { facility: 'stadium', level: 1, cost: 5, buildWeeks: 2, effects: effects({ stadiumCapacity: 18_000, weeklyRevenue: 0.25 }) },
    { facility: 'stadium', level: 2, cost: 10, buildWeeks: 3, effects: effects({ stadiumCapacity: 24_000, weeklyRevenue: 0.5 }) },
    { facility: 'stadium', level: 3, cost: 18, buildWeeks: 5, effects: effects({ stadiumCapacity: 32_000, weeklyRevenue: 0.9 }) },
  ],
  academy: [
    { facility: 'academy', level: 1, cost: 4, buildWeeks: 2, effects: effects({ academyQuality: 1 }) },
    { facility: 'academy', level: 2, cost: 9, buildWeeks: 3, effects: effects({ academyQuality: 2 }) },
    { facility: 'academy', level: 3, cost: 15, buildWeeks: 4, effects: effects({ academyQuality: 3 }) },
  ],
}

export const FACILITY_CANCEL_REFUND_RATE = 0.5
const FACILITY_TYPES = Object.keys(FACILITY_CATALOGUE) as FacilityType[]

const isLevel = (value: unknown): value is FacilityLevel => Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 3

export const facilityUpgrade = (facility: FacilityType, level: FacilityLevel): FacilityUpgrade | undefined =>
  level === 0 ? undefined : FACILITY_CATALOGUE[facility].find(upgrade => upgrade.level === level)

export const calculateFacilityEffects = (levels: Record<FacilityType, FacilityLevel>): FacilityEffects => {
  const result = { ...EMPTY_EFFECTS }
  for (const facility of FACILITY_TYPES) {
    const upgrade = facilityUpgrade(facility, levels[facility])
    if (!upgrade) continue
    if (facility === 'training-ground') result.developmentCapacityBonus = upgrade.effects.developmentCapacityBonus
    if (facility === 'medical-centre') {
      result.medicalRecoveryBonus = upgrade.effects.medicalRecoveryBonus
      result.injuryRiskReduction = upgrade.effects.injuryRiskReduction
    }
    if (facility === 'stadium') {
      result.stadiumCapacity = upgrade.effects.stadiumCapacity
      result.weeklyRevenue = upgrade.effects.weeklyRevenue
    }
    if (facility === 'academy') result.academyQuality = upgrade.effects.academyQuality
  }
  return result
}

export const createFacilitiesState = (baseDevelopmentCapacity = 10): FacilitiesState => ({
  version: 52,
  baseDevelopmentCapacity,
  levels: { 'training-ground': 0, 'medical-centre': 0, stadium: 0, academy: 0 },
  activeBuild: null,
  effects: { ...EMPTY_EFFECTS },
  processedRounds: [],
})

const applyEffects = (state: GameState, facilities: FacilitiesState): GameState => {
  const developmentCapacity = facilities.baseDevelopmentCapacity + facilities.effects.developmentCapacityBonus
  return {
    ...state,
    facilities,
    club: {
      ...state.club,
      developmentCapacity,
      resources: { ...state.club.resources, development: developmentCapacity },
    },
  }
}

export const hydrateFacilities = (state: GameState, saved?: Partial<FacilitiesState>): GameState => {
  const savedEffects = saved?.effects
  const inferredBase = Math.max(0, state.club.developmentCapacity - (Number(savedEffects?.developmentCapacityBonus) || 0))
  const baseDevelopmentCapacity = Number.isFinite(saved?.baseDevelopmentCapacity)
    ? Math.max(0, Number(saved!.baseDevelopmentCapacity))
    : inferredBase
  const levels = Object.fromEntries(FACILITY_TYPES.map(facility => [
    facility,
    isLevel(saved?.levels?.[facility]) ? saved!.levels![facility] : 0,
  ])) as Record<FacilityType, FacilityLevel>
  const effectsState = calculateFacilityEffects(levels)
  const candidate = saved?.activeBuild
  const upgrade = candidate && FACILITY_TYPES.includes(candidate.facility)
    ? facilityUpgrade(candidate.facility, candidate.targetLevel)
    : undefined
  const validBuild = Boolean(upgrade && candidate && candidate.targetLevel === levels[candidate.facility] + 1)
  const activeBuild = validBuild && candidate && upgrade ? {
    facility: candidate.facility,
    targetLevel: upgrade.level,
    cost: upgrade.cost,
    totalWeeks: upgrade.buildWeeks,
    weeksRemaining: Math.max(1, Math.min(upgrade.buildWeeks, Math.floor(Number(candidate.weeksRemaining) || upgrade.buildWeeks))),
    startedSeason: Math.max(1, Math.floor(Number(candidate.startedSeason) || state.season?.season || 1)),
    startedRound: Math.max(0, Math.floor(Number(candidate.startedRound) || 0)),
  } : null
  return applyEffects(state, {
    version: 52,
    baseDevelopmentCapacity,
    levels,
    activeBuild,
    effects: effectsState,
    processedRounds: Array.isArray(saved?.processedRounds) ? [...new Set(saved.processedRounds.filter(value => typeof value === 'string'))] : [],
  })
}

export const startFacilityBuild = (state: GameState, facility: FacilityType): GameState => {
  if (state.facilities.activeBuild) return state
  const nextLevel = state.facilities.levels[facility] + 1 as FacilityLevel
  const upgrade = facilityUpgrade(facility, nextLevel)
  if (!upgrade || state.club.resources.cash < upgrade.cost) return state
  return {
    ...state,
    facilities: {
      ...state.facilities,
      activeBuild: {
        facility,
        targetLevel: upgrade.level,
        cost: upgrade.cost,
        totalWeeks: upgrade.buildWeeks,
        weeksRemaining: upgrade.buildWeeks,
        startedSeason: state.season?.season ?? 1,
        startedRound: state.round,
      },
    },
    club: {
      ...state.club,
      resources: { ...state.club.resources, cash: state.club.resources.cash - upgrade.cost },
    },
  }
}

export const cancelFacilityBuild = (state: GameState): GameState => {
  const build = state.facilities.activeBuild
  if (!build) return state
  const refund = Math.round(build.cost * FACILITY_CANCEL_REFUND_RATE * 100) / 100
  return {
    ...state,
    facilities: { ...state.facilities, activeBuild: null },
    club: { ...state.club, resources: { ...state.club.resources, cash: state.club.resources.cash + refund } },
  }
}

export const completeFacilityBuild = (state: GameState): GameState => {
  const build = state.facilities.activeBuild
  if (!build || build.weeksRemaining > 0) return state
  const levels = { ...state.facilities.levels, [build.facility]: build.targetLevel }
  const facilities: FacilitiesState = {
    ...state.facilities,
    levels,
    activeBuild: null,
    effects: calculateFacilityEffects(levels),
  }
  return applyEffects(state, facilities)
}

export const advanceFacilityConstruction = (state: GameState, completedRound: number): GameState => {
  const roundKey = `s${state.season?.season ?? 1}-r${completedRound}`
  if (state.facilities.processedRounds.includes(roundKey)) return state
  const processedRounds = [...state.facilities.processedRounds, roundKey]
  const build = state.facilities.activeBuild
  if (!build) return { ...state, facilities: { ...state.facilities, processedRounds } }
  if (build.weeksRemaining > 1) {
    return {
      ...state,
      facilities: {
        ...state.facilities,
        processedRounds,
        activeBuild: { ...build, weeksRemaining: build.weeksRemaining - 1 },
      },
    }
  }
  return completeFacilityBuild({
    ...state,
    facilities: {
      ...state.facilities,
      processedRounds,
      activeBuild: { ...build, weeksRemaining: 0 },
    },
  })
}
